import {
  CreateEditRevisionDto,
  CreateProgram,
  ProgramRevision,
  ProgramWithDetail,
  QueryProgramsReq,
  SubmitForReviewDto,
  UpdateProgram,
  UpsertProgramQuestions
} from "@humanex/interfaces";
import { getErrorMessage, toast, useI18n } from "@humanex/ui/libs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { programApiClient } from "@/libs/api";

export const programKeys = {
  all: ["programs"] as const,
  lists: () => [...programKeys.all, "list"] as const,
  list: (filters: Partial<QueryProgramsReq>) => [...programKeys.lists(), filters] as const,
  details: () => [...programKeys.all, "detail"] as const,
  detail: (id: string) => [...programKeys.details(), id] as const,
  revisions: (id: string) => [...programKeys.detail(id), "revisions"] as const,
  questions: (id: string) => [...programKeys.all, id, "questions"] as const
};

export function useProgramsQuery(params: Partial<QueryProgramsReq> = {}) {
  return useQuery({
    queryKey: programKeys.list(params),
    queryFn: () => programApiClient.getList(params),
    staleTime: 5 * 60 * 1000
  });
}

export function useProgramQuery(id: string) {
  return useQuery({
    queryKey: programKeys.detail(id),
    queryFn: () => programApiClient.getOne(id),
    enabled: !!id,
    staleTime: 10 * 60 * 1000
  });
}

export function useProgramRevisions(id: string): { data: ProgramRevision[] | undefined; isLoading: boolean } {
  return useQuery({
    queryKey: programKeys.revisions(id),
    queryFn: () => programApiClient.getRevisions(id),
    enabled: !!id,
    staleTime: 30 * 1000
  });
}

export function useCreateProgram(
  options: {
    onSuccess?: (result: ProgramWithDetail) => void;
  } = {}
) {
  const { t } = useI18n();
  return useMutation({
    mutationFn: (data: CreateProgram) => programApiClient.create(data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useUpdateProgram(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProgram }) => programApiClient.update(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: programKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useSubmitForReview(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SubmitForReviewDto }) =>
      programApiClient.submitForReview(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: programKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function usePublishProgram(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => programApiClient.publishProgram(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: programKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useCreateEditRevision(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateEditRevisionDto }) =>
      programApiClient.createEditRevision(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: programKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function usePauseProgram(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => programApiClient.pauseProgram(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: programKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useResumeProgram(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => programApiClient.resumeProgram(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: programKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useCloseProgram(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => programApiClient.closeProgram(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: programKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useDeleteProgram(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => programApiClient.deleteProgram(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: programKeys.lists() });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useUpsertProgramQuestions(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpsertProgramQuestions }) =>
      programApiClient.upsertQuestions(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: programKeys.questions(variables.id) });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}
